// Who may author a wheel, and what a wheel's lifecycle state is. Shared by the route guard and the shell.
import { Panel, PanelStatus } from "../../types/geneCodes/Panel";
import { staffCan } from "../userPrefs/validatePermissions";

export type WheelNodeKind = "panel" | "trait" | "gene" | "snp";

// S3's backfill sets every existing row to 'published', so an absent status is a published wheel, never a draft.
export const panelStatusOf = (panel: Panel | null | undefined): PanelStatus => panel?.status ?? "published";

export const isDraftPanel = (panel: Panel | null | undefined): boolean => panelStatusOf(panel) === "draft";

export const isHiddenPanel = (panel: Panel | null | undefined): boolean => panelStatusOf(panel) === "hidden";

// One wording for both assignment pickers: a hidden wheel is assignable, just never shown.
export const HIDDEN_WHEEL_ASSIGNED_NOTE = "Assigned patients won't see this wheel until it is unhidden.";

// Cosmetic: labiq's authoring gate is the boundary (labiq.genecodes.author plus a staff caller); this only hides a builder every save would refuse.
export const canAuthorWheels = (): boolean => staffCan("labiq.genecodes.author");

// R6: a gene IS a record — a trait's child is a gene node, authored with or without SNPs under it.
export const childKindOf = (parent: WheelNodeKind): WheelNodeKind | null => {
	if (parent === "panel") return "trait";
	if (parent === "trait") return "gene";
	if (parent === "gene") return "snp";
	return null;
};

const KIND_LABEL: Record<WheelNodeKind, string> = { panel: "wheel", trait: "trait", gene: "gene", snp: "SNP" };

export const kindLabel = (kind: WheelNodeKind): string => KIND_LABEL[kind];

export type BuilderAction = "create" | "add" | "edit";

export interface BuilderTask {
	action: BuilderAction;
	kind: WheelNodeKind;
}

const ACTIONS: BuilderAction[] = ["create", "add", "edit"];
const KINDS: WheelNodeKind[] = ["panel", "trait", "gene", "snp"];

// The shell hands the builder its pending task as one `action:kind` string; "research" and anything
// unrecognised parse to null so a task that is not a node edit opens no editor.
export const parseBuilderTask = (task: string): BuilderTask | null => {
	const [action, kind] = task.split(":");
	if (!ACTIONS.includes(action as BuilderAction) || !KINDS.includes(kind as WheelNodeKind)) return null;
	return { action: action as BuilderAction, kind: kind as WheelNodeKind };
};
